import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import en from 'src/constants/en'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import { ContentAnalysisApiService } from 'src/services/contentAnalysisApi'
import {
  ContentAnalysisErrorResponse,
  ContentAnalysisPayload,
  ContentAnalysisSingleResponse,
  ContentAnalysisStatus,
  ContentAnalysisValidationErrorResponse,
} from 'src/services/contentAnalysisApi/interfaces/contentAnalysis.interface'

interface UseCreateContentAnalysisMutationParams {
  onError?: () => void
  onSuccess?: (data: ContentAnalysisSingleResponse) => void
}

type ApiErrorResponse =
  | ContentAnalysisErrorResponse
  | ContentAnalysisValidationErrorResponse

const getErrorMessage = (error: AxiosError<ApiErrorResponse>): string => {
  const data = error.response?.data
  if (!data) return en.CONTENT_ANALYSIS_CREATE_ERROR

  // Check for validation errors array
  if (
    'errors' in data &&
    Array.isArray(data.errors) &&
    data.errors.length > 0
  ) {
    return data.errors.join(', ')
  }

  // Check for single error message
  if (data.error) {
    return data.error
  }

  return en.CONTENT_ANALYSIS_CREATE_ERROR
}

const createContentAnalysis = async (
  payload: ContentAnalysisPayload
): Promise<ContentAnalysisSingleResponse> => {
  return ContentAnalysisApiService.create(payload)
}

export const useCreateContentAnalysisMutation = ({
  onError,
  onSuccess,
}: UseCreateContentAnalysisMutationParams = {}) => {
  const queryClient = useQueryClient()

  const notifySuccess = (status?: ContentAnalysisStatus) => {
    toast.success(
      status === 'draft'
        ? en.CONTENT_ANALYSIS_DRAFT_SUCCESS
        : en.CONTENT_ANALYSIS_CREATE_SUCCESS
    )
  }

  return useMutation({
    mutationFn: createContentAnalysis,
    onError: (error: AxiosError<ApiErrorResponse>) => {
      toast.error(getErrorMessage(error))
      onError?.()
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.GET_CONTENT_ANALYSIS_LIST],
      })
      notifySuccess(data?.data?.status)
      onSuccess?.(data)
    },
  })
}
